import React from 'react';
import { graphql } from 'gatsby';
import styled from '@emotion/styled';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowRight } from '@fortawesome/pro-light-svg-icons';

import Background from '@/components/atoms/Background';
import Container from '@/components/atoms/Container';
import Tag from '@/components/atoms/Tag';
import Button from '@/components/atoms/Button';
import Grid from '@/components/atoms/Grid';
import Typography from '@/components/atoms/Typography';
import Link from '@/components/atoms/Link';

import mq from '@/styles/mq';

import ImageDataType from '@/types/data/image';

interface ArticleProps {
  title?: React.ReactNode;
  content?: string | null;
  background?: ImageDataType;
  uid?: string;
}

interface Props {
  main?: ArticleProps;
  articles?: ArticleProps[];
}

const BlogHeroRoot = styled('div')`
  background-color: ${({ theme }) => theme.color.secondary.main};
  padding-top: ${({ theme }) => theme.spacing(5)};
  padding-bottom: ${({ theme }) => theme.spacing(8)};

  ${mq('lg')} {
    padding-top: ${({ theme }) => theme.spacing(15)};
  }
`;
const ParentGrid = styled(Grid)`
  align-items: center;
`;
const MainGrid = styled(Grid)`
  align-items: center;
`;
const MainLeftGrid = styled(Grid)`
  padding-right: 0;
  display: flex;
  flex-direction: column;
  align-items: flex-start;

  ${mq('lg')} {
    padding-right: ${({ theme }) => theme.spacing(4)};
  }
`;
const MainTag = styled(Tag)`
  margin-bottom: ${({ theme }) => theme.spacing(1)};
  padding-top: ${({ theme }) => theme.spacing(0.3)};
  padding-bottom: ${({ theme }) => theme.spacing(0.3)};
  padding-left: ${({ theme }) => theme.spacing(2)};
  padding-right: ${({ theme }) => theme.spacing(2)};
  ${mq('lg')} {
    font-size: 1.6rem;
  }
`;
const MainTitle = styled(Typography)`
  margin-bottom: ${({ theme }) => theme.spacing(1)};
`;
const MainContent = styled(Typography)`
  font-weight: 500;
  margin-top: ${({ theme }) => theme.spacing(1.5)};
  max-width: 500px;
  text-align: center;

  ${mq('lg')} {
    margin-top: 0;
    max-width: 100%;
    text-align: left;
  }
`;
const MainBg = styled(Background)`
  display: none;

  ${mq('lg')} {
    display: block;
    height: 580px;
    width: 100%;
    border-radius: 10px;
  }
`;
const MainMobileBg = styled(Background)`
  height: 350px;
  width: 250px;
  border-radius: 10px;

  ${mq('md')} {
    width: 450px;
  }

  ${mq('lg')} {
    display: none;
  }
`;
const ArticleRoot = styled('div')``;

const ArticleTag = styled(Tag)`
  margin-bottom: ${({ theme }) => theme.spacing(1)};
  padding-top: ${({ theme }) => theme.spacing(0.3)};
  padding-bottom: ${({ theme }) => theme.spacing(0.3)};
  padding-left: ${({ theme }) => theme.spacing(2)};
  padding-right: ${({ theme }) => theme.spacing(2)};
  ${mq('md')} {
    font-size: 1.6rem;
  }
`;
const ArticleTitleContainer = styled('div')`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;
const ArticleTitle = styled(Typography)`
  margin-bottom: ${({ theme }) => theme.spacing(1)};
  transition: 0.2s;
  &:hover {
    color: ${({ theme }) => theme.color.primary.main};
  }
`;
const Arrow = styled(FontAwesomeIcon)`
  font-size: 2.4rem;
`;
const ArticleContent = styled(Typography)`
  font-weight: 500;
  margin-bottom: ${({ theme }) => theme.spacing(1.5)};
`;
const ArticlesGrid = styled(Grid)`
  padding-left: ${({ theme }) => theme.spacing(5)};
  display: none;

  ${mq('lg')} {
    display: block;
  }

  & ${ArticleRoot}:not(:last-of-type) {
    border-bottom: 1px solid #e0e0e0;
    margin-bottom: ${({ theme }) => theme.spacing(2)};
  }
`;

const GridBg = styled(Grid)`
  position: relative;
`;

const ButtonBackground = styled(Button)`
  position: absolute;
  bottom: 111px;
  left: 0;
  ${mq('xxs')} {
    bottom: 87px;
    left: 0;
  }
  ${mq('xs')} {
    bottom: 63px;
    left: 0;
  }
  ${mq('lg')} {
    bottom: 0;
    left: 0;
  }
  z-index: 600;
  padding-top: ${({ theme }) => theme.spacing(0.7)};
  padding-bottom: ${({ theme }) => theme.spacing(0.7)};
  padding-left: ${({ theme }) => theme.spacing(2.2)};
  padding-right: ${({ theme }) => theme.spacing(2.2)};
  border-top-left-radius: 0px;
  border-bottom-right-radius: 0px;
`;

const Article = ({ title, content, uid }: ArticleProps): JSX.Element => {
  const characterLimit = 120;

  const substrContent = `${content?.substring(0, characterLimit)}...`;

  return (
    <ArticleRoot>
      <ArticleTag label="Tendances" />
      <ArticleTitleContainer>
        <Link to={uid || ''}>
          <ArticleTitle variant="h4">{title}</ArticleTitle>
        </Link>
        <Link to={uid || ''}>
          <Arrow icon={faArrowRight} />
        </Link>
      </ArticleTitleContainer>
      <ArticleContent>{substrContent}</ArticleContent>
    </ArticleRoot>
  );
};

const BlogHero = ({ main, articles }: Props): JSX.Element => {
  const { background, title, content, uid } = main || {};

  const characterLimit = 120;

  const substrContent = `${content?.substring(0, characterLimit)}...`;

  const Articles = articles?.map((article, index) => (
    <Article key={index.toString()} {...article} />
  ));

  return (
    <BlogHeroRoot>
      <Container maxWidth="lg">
        <ParentGrid container>
          <MainGrid container xxs={12} lg={7.5}>
            <MainLeftGrid xxs={12} lg={6}>
              <MainTag label="Tendances" />
              <MainTitle variant="h3">{title}</MainTitle>
              <MainMobileBg
                overlay={0.2}
                background={background}
                imageProps={{ width: 450, height: 680 }}
              />
              <MainContent>{substrContent}</MainContent>
            </MainLeftGrid>
            <GridBg lg={6}>
              <MainBg
                overlay={0.2}
                background={background}
                //imageProps={{ width: 450, height: 680 }}
              />
              <ButtonBackground
                variant="contained"
                as={Link}
                {...{ to: `${uid}` }}
              >
                En savoir plus
              </ButtonBackground>
            </GridBg>
          </MainGrid>
          <ArticlesGrid lg={4.5}>{Articles}</ArticlesGrid>
        </ParentGrid>
      </Container>
    </BlogHeroRoot>
  );
};

export const query = graphql`
  fragment LastArticles on PrismicArticlePageDataType {
    publication_date
    title {
      text
    }
    content {
      text
    }
    hero_background {
      url
      alt
    }
  }
`;

// export const query = graphql`
//   fragment BlogHero on PrismicBlogPageDataType {
//     main_article {
//       document {
//         ... on PrismicArticlePage {
//           id
//           data {
//             hero_background {
//               url
//               alt
//             }
//             title {
//               raw
//             }
//             content {
//               text
//             }
//           }
//         }
//       }
//     }
//     articles {
//       article {
//         document {
//           ... on PrismicArticlePage {
//             id
//             uid
//             data {
//               title {
//                 raw
//               }
//               content {
//                 text
//               }
//             }
//           }
//         }
//       }
//     }
//   }
// `;

export default BlogHero;
